import React, { CSSProperties, ForwardedRef, forwardRef, useEffect } from 'react'
import { InputBase, Paper } from '@mui/material'
import { autocompleteLocation, LocationData } from 'services/location'
import { PinDrop } from '@mui/icons-material'
import Condition from 'components/Condition'
import { Nullable } from 'types/global'
import useStyles from './styles'
import AddressSuggestions from './components/AddressSuggestions'

const ENTER_KEY_CODE = 13

export interface IAddressProps {
  onSelectLocation?: (event: any, suggestion: LocationData) => void
  editable?: boolean
  initialValue?: Nullable<string>
  loading?: boolean
  label?: string
  style?: CSSProperties
}

function Address(props: IAddressProps, ref: ForwardedRef<HTMLDivElement>) {
  const { classes } = useStyles()
  const { initialValue = '', label = 'Address', editable, onSelectLocation, loading, ...otherProps } = props

  const [value, setValue] = React.useState(initialValue || '')
  const [suggestions, setSuggestions] = React.useState<LocationData[]>([])
  const [loadingSuggestions, setLoadingSuggestions] = React.useState(false)
  const [locationSelected, setLocationSelected] = React.useState(false)
  const [changed, setChanged] = React.useState(false)

  const resultsOpen = (suggestions.length > 0 || loadingSuggestions)

  const selectLocation = (event: React.ChangeEvent<any>, suggestion: LocationData) => {
    if (onSelectLocation) {
      onSelectLocation(event, suggestion)
    }

    setSuggestions([])
    setLocationSelected(true)
  }

  const updateSuggestions = async (val: string) => {
    setLoadingSuggestions(true)
    setSuggestions(await autocompleteLocation(val))
    setLoadingSuggestions(false)
  }

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setChanged(true)
    setLocationSelected(false)
    setLoadingSuggestions(true)

    const val = e.target.value
    setValue(val)

    await new Promise(r => setTimeout(r, 0)) // ????

    updateSuggestions(val)
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value && suggestions.length === 0) {
      setLoadingSuggestions(true)
      updateSuggestions(e.target.value)
    }
  }

  const handleBlur = (e: React.FocusEvent<any>) => {
    if (locationSelected || !changed) {
      setLoadingSuggestions(false)
      setSuggestions([])
    }
  }

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.which === ENTER_KEY_CODE && suggestions.length > 0) {
      selectLocation(e, suggestions[0])
    }
  }

  useEffect(() => {
    // Automatically reset the current value when the initial value changes
    setValue(initialValue || '')
  }, [initialValue])

  return (
    <div className={classes.container} ref={ref} {...otherProps}>
      <Paper className={classes.paper}>
        <div className={classes.field}>
          <PinDrop className={classes.icon} />

          <InputBase
            value={value || ''} // Avoid uncontrolled input when passing undefined accidentally
            onChange={handleInputChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onKeyDown={handleInputKeyDown}
            name="location"
            placeholder={label}
            data-cy="address"
            fullWidth
            disabled={!editable || loading}
          />
        </div>

        <Condition condition={resultsOpen}>
          <AddressSuggestions
            changed={changed}
            loadingSuggestions={loadingSuggestions}
            suggestions={suggestions}
            locationSelected={locationSelected}
            onSelectLocation={selectLocation}
          />
        </Condition>
      </Paper>
    </div>
  )
}

export default React.memo(forwardRef(Address))
